import {
  EGiftRegistryItemStatus,
  type IGiftRegistryPublicItem,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';

import PaymentProgressOverlay from '../components/payment-progress-overlay';
import EmailVerifyBadge from '../fragments/email-verify-badge';
import PrivacyNoticeLink from '../fragments/privacy-notice-link';
import { useEmailVerification } from '../fragments/use-email-verification';
import { type IConfigState } from '../reducers/config/types';
import { type RootState } from '../reducers/customer';
import { EEmailValidatorCorrelationId } from '../reducers/email-validator/types';
import { giftRegistryContribute } from '../reducers/gift-registry-public/reducer';
import { type IGiftRegistryPublicState } from '../reducers/gift-registry-public/types';
import { type ILanguageState } from '../reducers/language/types';
import { formatDate, formatPrice } from '../utils/browser-format';

interface IProps {
  Config: IConfigState;
  GiftRegistryPublic: IGiftRegistryPublicState;
  Language: ILanguageState;
  dispatch: Dispatch;
}

interface IContributeItemProps {
  item: IGiftRegistryPublicItem;
  target: number | null;
  remaining: number;
  currency: string;
  amount: string;
  onAmountChange: (itemUUID: string, amount: string) => void;
}

const ContributeItem = ({
  item,
  target,
  remaining,
  currency,
  amount,
  onAmountChange,
}: IContributeItemProps): React.JSX.Element => {
  const { t } = useTranslation();

  const setAmount = useCallback(
    (value: number): void => {
      onAmountChange(item.uuid, value.toFixed(2));
    },
    [item.uuid, onAmountChange],
  );

  const quarterAmount =
    target !== null ? Number((target * 0.25).toFixed(2)) : null;
  const halfAmount = target !== null ? Number((target * 0.5).toFixed(2)) : null;

  return (
    <div className="gift-registry-public__amount-row">
      <label htmlFor={`contribute-amount-${item.uuid}`}>
        {t('giftRegistryPublic.formAmount')} ({currency})
      </label>
      <div className="gift-registry-public__amount-input-group">
        <input
          id={`contribute-amount-${item.uuid}`}
          type="number"
          min="0"
          max={remaining}
          step="0.01"
          value={amount}
          onChange={(event): void =>
            onAmountChange(item.uuid, event.target.value)
          }
        />
        <div className="gift-registry-public__percent-buttons">
          {quarterAmount !== null && quarterAmount <= remaining && (
            <button
              type="button"
              className="gift-registry-public__percent-btn"
              onClick={(): void => setAmount(quarterAmount)}
            >
              25%
            </button>
          )}
          {halfAmount !== null && halfAmount <= remaining && (
            <button
              type="button"
              className="gift-registry-public__percent-btn"
              onClick={(): void => setAmount(halfAmount)}
            >
              50%
            </button>
          )}
          <button
            type="button"
            className="gift-registry-public__percent-btn"
            onClick={(): void => setAmount(remaining)}
          >
            {t('giftRegistryPublic.amountRemaining')}
          </button>
        </div>
      </div>
    </div>
  );
};

function GiftRegistryPublicContainer({
  Config,
  GiftRegistryPublic,
  Language,
  dispatch,
}: IProps): React.JSX.Element {
  const { t } = useTranslation();
  const { shareToken: shareTokenParam } = useParams<{ shareToken: string }>();
  const shareToken = GiftRegistryPublic.shareToken ?? shareTokenParam ?? '';
  const giftRegistry = GiftRegistryPublic.giftRegistry;
  const languageCode = Language.languageType.code;

  const [donorName, setDonorName] = useState<string>('');
  const [donorEmail, setDonorEmail] = useState<string>('');
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [localError, setLocalError] = useState<boolean>(false);

  const emailVerification = useEmailVerification({
    correlationId: EEmailValidatorCorrelationId.GiftRegistryContribution,
    email: donorEmail.trim(),
    enabled: Config.emailValidator.giftRegistryContribution,
  });

  const handleAmountChange = useCallback(
    (itemUUID: string, amount: string): void => {
      setAmounts((previous) => ({ ...previous, [itemUUID]: amount }));
    },
    [],
  );

  useEffect((): void => {
    if (GiftRegistryPublic.contributeRedirectLink) {
      window.location.href = GiftRegistryPublic.contributeRedirectLink;
    }
  }, [GiftRegistryPublic.contributeRedirectLink]);

  const contributableItems = useMemo(
    () =>
      (giftRegistry?.items ?? [])
        .filter((item) => item.status === EGiftRegistryItemStatus.ACTIVE)
        .map((item) => {
          const target = item.targetPrice;
          const remaining = target !== null ? target - item.collectedAmount : 0;
          return { item, remaining };
        })
        .filter(
          ({ remaining, item }) => item.targetPrice === null || remaining > 0,
        ),
    [giftRegistry],
  );

  const totalAmount = useMemo(
    () =>
      contributableItems.reduce((sum, { item }) => {
        const numericAmount = Number(amounts[item.uuid]);
        return Number.isFinite(numericAmount) && numericAmount > 0
          ? sum + numericAmount
          : sum;
      }, 0),
    [contributableItems, amounts],
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();

      const items = contributableItems
        .map(({ item, remaining }) => ({
          itemUUID: item.uuid,
          amount: Number(amounts[item.uuid]),
          remaining,
        }))
        .filter(({ amount }) => Number.isFinite(amount) && amount > 0);

      if (
        !donorName ||
        !donorEmail ||
        items.length === 0 ||
        items.some(({ amount, remaining }) => amount > remaining)
      ) {
        setLocalError(true);
        return;
      }

      setLocalError(false);

      dispatch(
        giftRegistryContribute({
          shareToken,
          donorName,
          donorEmail,
          items: items.map(({ itemUUID, amount }) => ({ itemUUID, amount })),
          languageCode,
        }),
      );
    },
    [
      shareToken,
      donorName,
      donorEmail,
      contributableItems,
      amounts,
      languageCode,
      dispatch,
    ],
  );

  if (!giftRegistry) {
    return (
      <div className="gift-registry-public">
        <p>{t('giftRegistryPublic.notFound')}</p>
      </div>
    );
  }

  const currency = giftRegistry.currencyType.isoCode;

  return (
    <form className="gift-registry-public" onSubmit={handleSubmit}>
      <h1>{giftRegistry.title}</h1>
      <p className="gift-registry-public__owner">
        {t('giftRegistryPublic.byOwner', {
          name: giftRegistry.ownerDisplayName,
        })}
      </p>
      <p className="gift-registry-public__expires">
        {t('giftRegistryPublic.expiresAt')} {formatDate(giftRegistry.expiresAt)}
      </p>

      {contributableItems.length > 0 && (
        <div className="gift-registry-public__donor-form">
          <label htmlFor="contribute-name">
            {t('giftRegistryPublic.formDonorName')}
          </label>
          <input
            id="contribute-name"
            type="text"
            value={donorName}
            maxLength={100}
            onChange={(event): void => setDonorName(event.target.value)}
            required
          />

          <label htmlFor="contribute-email">
            {t('giftRegistryPublic.formDonorEmail')}
          </label>
          <input
            id="contribute-email"
            type="email"
            value={donorEmail}
            onChange={(event): void => setDonorEmail(event.target.value)}
            onBlur={emailVerification.onBlur}
            required
          />
          <EmailVerifyBadge
            status={emailVerification.status}
            suggestedEmail={emailVerification.suggestedEmail}
            onUseSuggestion={setDonorEmail}
          />
        </div>
      )}

      <ul className="gift-registry-public__items">
        {giftRegistry.items.map((item) => {
          const target = item.targetPrice;
          const progress =
            target && target > 0
              ? Math.min(100, Math.round((item.collectedAmount / target) * 100))
              : 0;
          const remaining = target !== null ? target - item.collectedAmount : 0;
          const isFullyFunded = target !== null && remaining <= 0;

          return (
            <li key={item.uuid} className="gift-registry-public__item">
              <h2>{item.product.name ?? item.product.uuid}</h2>
              {item.note && <p>{item.note}</p>}

              {target !== null && (
                <>
                  <div className="gift-registry-public__progress-bar">
                    <div
                      className="gift-registry-public__progress-bar-fill"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p>
                    {formatPrice(item.collectedAmount, currency)}{' '}
                    {t('giftRegistryPublic.of')} {formatPrice(target, currency)}{' '}
                    ({progress}%)
                  </p>
                </>
              )}

              {item.contributions.length > 0 && (
                <ul className="gift-registry-public__contributions">
                  {item.contributions.map((contribution, index) => (
                    <li key={`${contribution.donorName}-${index}`}>
                      {contribution.donorName} —{' '}
                      {formatPrice(contribution.amount, currency)}
                    </li>
                  ))}
                </ul>
              )}

              {item.status === EGiftRegistryItemStatus.ACTIVE &&
                !isFullyFunded && (
                  <ContributeItem
                    item={item}
                    target={target}
                    remaining={remaining}
                    currency={currency}
                    amount={amounts[item.uuid] ?? ''}
                    onAmountChange={handleAmountChange}
                  />
                )}
              {item.status === EGiftRegistryItemStatus.ACTIVE &&
                isFullyFunded && <p>{t('giftRegistryPublic.itemFunded')}</p>}
              {item.status === EGiftRegistryItemStatus.CONFIRMED && (
                <p>{t('giftRegistryPublic.itemConfirmed')}</p>
              )}
              {item.status === EGiftRegistryItemStatus.EXPIRED && (
                <p>{t('giftRegistryPublic.itemExpired')}</p>
              )}
            </li>
          );
        })}
      </ul>

      {contributableItems.length > 0 && (
        <div className="gift-registry-public__submit-bar">
          {(localError || GiftRegistryPublic.contributeError) && (
            <p className="gift-registry-public__form-error">
              {t('giftRegistryPublic.formError')}
            </p>
          )}
          <PaymentProgressOverlay
            isOpen={
              GiftRegistryPublic.isContributing ||
              Boolean(GiftRegistryPublic.contributeRedirectLink)
            }
          />
          <PrivacyNoticeLink />
          <button
            type="submit"
            className="gift-registry-public__submit"
            disabled={GiftRegistryPublic.isContributing || totalAmount <= 0}
          >
            {t('giftRegistryPublic.formSubmit')} —{' '}
            {formatPrice(totalAmount, currency)}
          </button>
        </div>
      )}
    </form>
  );
}

const mapStateToProps = ({
  Config,
  GiftRegistryPublic,
  Language,
}: RootState): Omit<IProps, 'dispatch'> => ({
  Config,
  GiftRegistryPublic,
  Language,
});

export default connect(mapStateToProps)(GiftRegistryPublicContainer);
